// ---- Domain types shared across the core ----
export type TransferStatus = "prepare" | "wait" | "transfer" | "process";

export type FileHeader = { name: string; mime: string; size: number };

export type TransferRequest = {
  header: FileHeader[];
  totalSize: number;
  imagesOnly: boolean;
  thumbnailDataUrl: string;
};

export type SignalType = "offer" | "answer";

/** A signal queued to send to the server (to a specific peer). */
export type OutgoingSignal = { to: string; type: SignalType; payload: { sdp: RTCSessionDescriptionInit } };

/** A signal received from the server (from a specific peer). */
export type IncomingSignal = { from: string; type: SignalType; payload: { sdp: RTCSessionDescriptionInit } };

export type ServerConfig = { turnEnabled: boolean; buttons: Record<string, unknown> };

// ---- Event payload map (names preserved from the legacy window bus) ----
export type CoreEvents = {
  // connection / room
  config: ServerConfig;
  "display-name": { peerId: string; displayName: string; deviceName: string };
  "self-display-name-changed": string;
  "peer-joined": { peer: { peerId: string; displayName?: string; deviceName?: string } };
  "peer-left": { peerId: string };
  "peer-connected": { peerId: string; connectionHash: string };
  "peer-disconnected": string;
  "peer-display-name-changed": { peerId: string; displayName: string };
  "notify-peer-display-name-changed": string;
  // signaling
  signal: IncomingSignal;
  "signal-send": OutgoingSignal;
  "handshake-active": boolean;
  // transfer (driven by UI and peers)
  "files-selected": { to: string; files: File[] };
  "respond-to-files-transfer-request": { to: string; accepted: boolean };
  "send-text": { to: string; text: string };
  "ui-request-send-text": { to: string; displayName: string };
  "files-transfer-request": { request: TransferRequest; peerId: string };
  "file-transfer-accepted": undefined;
  "cancel-file-transfer": { to: string };
  "cancel-files-transfer-request": { peerId: string };
  "file-transfer-cancelled": { peerId: string };
  "set-progress": { peerId: string; progress: number; status: TransferStatus };
  "file-received": Blob;
  "files-received": { peerId: string; files: File[]; imagesOnly: boolean; totalSize: number };
  "files-sent": undefined;
  "text-received": { text: string; peerId: string };
  "unread-counts-updated": Record<string, number>;
  // misc / lifecycle
  "notify-user": string;
  beforeunload: Event;
  pagehide: undefined;
};

export type CoreEventName = keyof CoreEvents;
export type CoreEventHandler<K extends CoreEventName> = (detail: CoreEvents[K]) => void;

/** Typed event bus replacing the legacy global `window` Events. One instance is shared
 *  by the core services (and, in Phase 3, subscribed to by React hooks). */
export class CoreBus {
  private readonly target = new EventTarget();
  private readonly wrapped = new WeakMap<CoreEventHandler<CoreEventName>, EventListener>();

  on<K extends CoreEventName>(type: K, handler: CoreEventHandler<K>): () => void {
    const listener = ((e: Event) => handler((e as CustomEvent<CoreEvents[K]>).detail)) as EventListener;
    this.wrapped.set(handler as CoreEventHandler<CoreEventName>, listener);
    this.target.addEventListener(type, listener);
    return () => this.off(type, handler);
  }

  off<K extends CoreEventName>(type: K, handler: CoreEventHandler<K>): void {
    const listener = this.wrapped.get(handler as CoreEventHandler<CoreEventName>);
    if (listener) {
      this.target.removeEventListener(type, listener);
      this.wrapped.delete(handler as CoreEventHandler<CoreEventName>);
    }
  }

  emit<K extends CoreEventName>(type: K, detail: CoreEvents[K]): void {
    this.target.dispatchEvent(new CustomEvent(type, { detail }));
  }
}
